import { forwardRef, cloneElement, isValidElement } from "react";
import { Fade, Tooltip } from "@mui/material";
import { IHint } from "./Hint.props";
import { useTranslate } from "@/hooks";
import useLocalStorageState from "use-local-storage-state";
import { ReactMarkdown } from "@/ui";

const Hint = forwardRef<HTMLElement, IHint>(
	(
		{
			hintActive = false,
			position = "top",
			width = "auto",
			hintWidth = "280px",
			textAlign = "left",
			textOverflow = false,
			blockHeight,
			spanStyle,
			offset = [0, -8],
			disableFlip = false,
			disableOverflow = false,
			alwaysOpen,
			...props
		},
		ref
	) => {
		const { children, hint, ...rest } = props;
		const [storeEnabled] = useLocalStorageState("hintModeEnabled");
		const translate = useTranslate();
		const enabled = hintActive ? hintActive : storeEnabled;

		if (!enabled || !hint) {
			if (isValidElement(children)) {
				return cloneElement(children as React.ReactElement, { ref });
			}
			return <>{children}</>;
		}

		const child = isValidElement(children) ? (
			cloneElement(children as React.ReactElement<any>, { ref })
		) : (
			<span
				style={{
					display: "block",
					width,
					overflow: textOverflow ? "hidden" : "visible",
					height: blockHeight ?? "auto",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					...spanStyle,
				}}
				ref={ref}
			>
				{children}
			</span>
		);

		return (
			<Tooltip
				open={alwaysOpen ? true : undefined}
				TransitionComponent={Fade}
				TransitionProps={{ timeout: 0 }}
				placement={position}
				arrow
				title={
					<ReactMarkdown
						components={{
							p: ({ node, ...props }) => <p style={{ marginBottom: "6px" }} {...props} />,
							ol: ({ node, ...props }) => <ol style={{ padding: "0 0 0 16px" }} {...props} />,
						}}
					>
						{translate(hint)}
					</ReactMarkdown>
				}
				slotProps={{
					popper: {
						modifiers: [
							{
								name: "offset",
								options: { offset },
							},
							{
								name: "flip",
								enabled: !disableFlip,
							},
							{
								name: "preventOverflow",
								enabled: !disableOverflow,
								options: {
									padding: 16,
									altAxis: true,
								},
							},
						],
					},
					tooltip: {
						onClick: (e: any) => e.stopPropagation(),
						onMouseDown: (e: any) => e.stopPropagation(),
						sx: {
							background: "#F8FAFF",
							color: "#918c8c",
							padding: "8px 8px",
							borderRadius: "6px",
							maxHeight: "90vh",
							border: "1px solid #D9E2F0",
							fontSize: "11px",
							lineHeight: 1.35,
							maxWidth: hintWidth,
							boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
							whiteSpace: "normal",
							textAlign,
						},
					},
					arrow: {
						sx: {
							color: "#F8FAFF",
							"&:before": {
								border: "1px solid #D9E2F0",
							},
						},
					},
				}}
				{...rest}
			>
				{/* <span
					ref={ref}
					style={{
						display: "block",
						width,
						overflow: textOverflow ? "hidden" : "visible",
						height: blockHeight ?? "auto",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						...spanStyle,
					}}
				>
					{children}
				</span> */}
				{child}
			</Tooltip>
		);
	}
);

export default Hint;
